// ============================================================
// Compte rendu d'une promotion d'un bloc : le bandeau en tête de carte
// ============================================================
// Fonctions pures (testées par promotion.report.test.ts). Le bandeau dit en clair où en est le
// proxy (« Proxy arrêté à… », « Proxy redémarré à… », « Proxy toujours arrêté »), le bilan du
// CICD et quoi faire, et quels gestes proposer (Démarrer le proxy, Aligner l'egg). Les heures sont
// celles du fuseau du navigateur ; `timeZone` ne sert qu'aux tests.
// ============================================================

import type { PromotionJobSnapshot } from '../services/promotion.service';

export type BannerTone = 'success' | 'info' | 'warning' | 'danger';

export interface ProxyBanner {
  tone: BannerTone;
  title: string;
  /** Bilan du CICD, puis quoi faire. */
  lines: string[];
  /** Étapes non jouées après un arrêt. */
  notRun: string[];
  /** Geste « Démarrer le proxy » : promotion finie, proxy laissé arrêté. */
  canStartProxy: boolean;
  /** Geste « Aligner l'egg sur les serveurs » : un egg est resté derrière ses serveurs. */
  canAlignEggs: boolean;
}

const NBSP = ' ';

/** Heure « 09:50:12 ». */
export function fmtTime(iso: string | undefined, timeZone?: string): string {
  if (!iso) return '?';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '?';
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone,
  }).format(d);
}

/** Durée « 45 s », « 8 min 28 s », « 1 h 02 min ». */
export function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '?';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}${NBSP}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}${NBSP}min ${String(s % 60).padStart(2, '0')}${NBSP}s`;
  return `${Math.floor(m / 60)}${NBSP}h ${String(m % 60).padStart(2, '0')}${NBSP}min`;
}

function interruption(job: PromotionJobSnapshot): string {
  if (!job.proxyStoppedAt || !job.proxyStartedAt) return '';
  const ms = Date.parse(job.proxyStartedAt) - Date.parse(job.proxyStoppedAt);
  return Number.isFinite(ms) && ms >= 0 ? ` (interruption de ${fmtDuration(ms)})` : '';
}

/** Libellé court de la phase d'une étape, pour la repérer dans la liste. */
export function phaseTag(phase: string | undefined): string | null {
  switch (phase) {
    case 'preflight': return 'Contrôle';
    case 'prepare': return 'Joueurs';
    case 'proxy-stop': return 'Proxy';
    case 'eggs': return 'Eggs';
    case 'body': return 'Mise à jour';
    case 'check': return 'Contrôle';
    case 'proxy-start': return 'Proxy';
    case 'reopen': return 'Réouverture';
    case 'rollback': return 'Remise en route';
    default: return null;
  }
}

/** Bandeau du proxy ; null pour une promotion d'avant la promotion d'un bloc. */
export function proxyBanner(job: PromotionJobSnapshot, timeZone?: string): ProxyBanner | null {
  if (!job.bloc && !job.outcome) return null;
  const at = (iso?: string) => fmtTime(iso, timeZone);
  const lines = [job.summary, ...(job.nextSteps ?? [])].filter((l): l is string => !!l);
  const notRun = job.notRun ?? [];
  const stopped = job.proxyState === 'stopped';
  const base = {
    lines, notRun,
    canStartProxy: job.done && stopped && !job.dryRun,
    canAlignEggs: job.done && (job.eggsBehind?.length ?? 0) > 0,
  };

  if (!job.done) {
    if (stopped) {
      return { ...base, tone: 'warning', lines: [], title: `Proxy arrêté depuis ${at(job.proxyStoppedAt)} : plus aucun joueur`
        + ' connecté pendant la mise à jour' };
    }
    if (job.proxyState === 'restarted') {
      return { ...base, tone: 'info', lines: [], title: `Proxy redémarré à ${at(job.proxyStartedAt)}, fin de la promotion en cours` };
    }
    return { ...base, tone: 'info', lines: [], title: job.dryRun
      ? 'Simulation à vide en cours : rien n\'est envoyé à Pterodactyl'
      : 'Proxy encore en marche : contrôle avant arrêt et préparation' };
  }

  switch (job.outcome) {
    case 'ok':
      return { ...base, tone: 'success', title: `Proxy arrêté à ${at(job.proxyStoppedAt)}, redémarré à ${at(job.proxyStartedAt)}`
        + interruption(job) };
    case 'simulated':
      return { ...base, tone: 'info', title: 'Simulation à vide : rien n\'a été envoyé à Pterodactyl' };
    case 'stored':
      return { ...base, tone: 'info', title: 'Procédure jouée telle qu\'enregistrée : pas de mise à jour d\'images, proxy non arrêté' };
    case 'refused':
      return { ...base, tone: 'danger', title: 'Promotion refusée : rien n\'a été arrêté, les joueurs n\'ont rien vu' };
    case 'cancelled':
      if (stopped) return { ...base, tone: 'danger', title: `Proxy toujours arrêté depuis ${at(job.proxyStoppedAt)} : les joueurs`
        + ' ne peuvent pas se connecter' };
      if (job.proxyState === 'restarted') return { ...base, tone: 'warning', title: `Promotion annulée : proxy arrêté à`
        + ` ${at(job.proxyStoppedAt)}, redémarré à ${at(job.proxyStartedAt)}, aucun serveur de jeu touché` };
      return { ...base, tone: 'warning', title: 'Promotion annulée avant l\'arrêt du proxy' };
    default:
      // halted, proxy-not-restarted, interrupted
      if (stopped) return { ...base, tone: 'danger', title: `Proxy toujours arrêté depuis ${at(job.proxyStoppedAt)} : les joueurs`
        + ' ne peuvent pas se connecter' };
      return { ...base, tone: 'danger', title: job.outcome === 'interrupted' ? 'Promotion interrompue' : 'Promotion en échec' };
  }
}

/** Libellé et teinte du badge d'état de la carte. */
export function outcomeBadge(job: PromotionJobSnapshot): { label: string; tone: 'info' | 'success' | 'danger' | 'warning' } | null {
  if (!job.done || !job.outcome) return null;
  switch (job.outcome) {
    case 'ok': return { label: 'Terminé ✓', tone: 'success' };
    case 'stored': return null;
    case 'simulated': return { label: 'Simulation', tone: 'info' };
    case 'refused': return { label: 'Refusée, rien touché', tone: 'danger' };
    case 'cancelled': return { label: job.proxyState === 'stopped' ? 'Annulée, proxy arrêté' : 'Annulée', tone: 'warning' };
    case 'halted': return { label: 'Arrêtée, proxy arrêté', tone: 'danger' };
    case 'proxy-not-restarted': return { label: 'Proxy pas redémarré', tone: 'danger' };
    case 'interrupted': return { label: 'Interrompue', tone: 'danger' };
    default: return null;
  }
}
